import React, { useEffect, useId, useRef, useState } from 'react';
import { coverReady, type Cover, type Request } from './model';
import { AdminApiError } from '../admin-api';
import { COVER_COPY } from './cover-copy';
import type { ReportLocale } from './report-copy';
const input = 'admin-input mt-2 block w-full';

export function CoverEditor({
  value,
  change,
  loadedUrl,
  loaded,
  failed,
  setFailed,
  request,
  uploading,
  setUploading,
  replacementOnly = false,
  locale = 'en',
}: {
  value: Cover;
  change(v: Cover): void;
  loadedUrl: string | null;
  loaded(v: string | null): void;
  failed: boolean;
  setFailed(v: boolean): void;
  request: Request;
  uploading: boolean;
  setUploading(v: boolean): void;
  replacementOnly?: boolean;
  locale?: ReportLocale;
}) {
  const uploadHelpId = useId();
  const copy = COVER_COPY[locale];
  const [source, setSource] = useState<'link' | 'upload'>('link');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const upload = async (file: File) => {
    setUploadError(null);
    if (
      !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
      !file.size ||
      file.size > 5 * 1024 * 1024
    ) {
      setUploadError(copy.invalid);
      return;
    }
    setUploading(true);
    try {
      const result = await request<{ url: string }>('/covers', file);
      if (mounted.current) update({ ...value, policy: 'moderator', url: result.url });
    } catch (error) {
      if (mounted.current)
        setUploadError(
          error instanceof AdminApiError && [400, 413].includes(error.status)
            ? copy.processingFailed
            : error instanceof AdminApiError && error.status === 429
              ? copy.rateLimited
              : copy.uploadFailed,
        );
    } finally {
      if (mounted.current) setUploading(false);
    }
  };
  const update = (v: Cover) => {
    loaded(null);
    setFailed(false);
    change(v);
  };
  const url = value.url ?? '';
  let safeUrl = false;
  try {
    const parsed = new URL(url);
    safeUrl = parsed.protocol === 'https:' && !parsed.username && !parsed.password;
  } catch {
    /* editable input */
  }
  return (
    <fieldset
      className="mt-7 min-w-0 border-t border-line/65 pt-5"
      disabled={uploading}
      aria-busy={uploading}
    >
      <legend className="pr-3 font-serif text-2xl">{copy.title}</legend>
      {!replacementOnly && (
        <label className="block text-sm font-semibold">
          {copy.policy}
          <select
            className={input}
            value={value.policy}
            onChange={(e) =>
              update(
                e.target.value === 'moderator'
                  ? { ...value, policy: 'moderator' }
                  : { policy: e.target.value as Cover['policy'] },
              )
            }
          >
            <option value="automatic">{copy.automatic}</option>
            <option value="moderator">{copy.moderator}</option>
            <option value="none">{copy.none}</option>
          </select>
        </label>
      )}
      {value.policy === 'none' && (
        <p className="mt-3 text-sm leading-6 text-ink-muted">{copy.noneHelp}</p>
      )}
      {value.policy === 'moderator' && (
        <>
          <div className="mt-4 flex flex-wrap gap-3" role="group" aria-label={copy.source}>
            <button
              type="button"
              className={source === 'link' ? 'admin-button-primary' : 'admin-button'}
              aria-pressed={source === 'link'}
              onClick={() => setSource('link')}
            >
              {copy.link}
            </button>
            <button
              type="button"
              className={source === 'upload' ? 'admin-button-primary' : 'admin-button'}
              aria-pressed={source === 'upload'}
              onClick={() => setSource('upload')}
            >
              {copy.upload}
            </button>
          </div>
          {source === 'link' ? (
            <label className="mt-4 block">
              {copy.url}
              <input
                className={input}
                type="url"
                value={url}
                onChange={(e) => update({ ...value, url: e.target.value })}
              />
            </label>
          ) : (
            <div className="mt-4 rounded-xl border border-dashed border-line bg-panel-raised p-5">
              <label className="block text-sm font-semibold">
                {copy.add}
                <input
                  className="mt-3 block w-full text-sm file:mr-4 file:rounded-lg file:border-0 file:bg-panel file:px-4 file:py-3 file:font-semibold file:text-ink"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  aria-describedby={uploadHelpId}
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    event.currentTarget.value = '';
                    if (file) void upload(file);
                  }}
                />
              </label>
              <p id={uploadHelpId} className="mt-3 text-sm leading-6 text-ink-muted">
                {copy.uploadHelp}
              </p>
            </div>
          )}
          {uploading && (
            <p role="status" className="mt-3 text-sm text-ink-muted">
              {copy.uploading}
            </p>
          )}
          {uploadError && (
            <p role="alert" className="mt-3 text-sm text-warning">
              {uploadError}
            </p>
          )}
          <label className="mt-4 block">
            {copy.rights}
            <textarea
              className={input}
              maxLength={2000}
              value={value.rightsNote ?? ''}
              onChange={(e) => change({ ...value, rightsNote: e.target.value })}
            />
          </label>
          {safeUrl && (
            <img
              key={url}
              src={url}
              alt={copy.preview}
              referrerPolicy="no-referrer"
              className="mt-4 max-h-64 max-w-48 rounded-lg bg-panel-raised p-3 object-contain"
              onLoad={() => {
                loaded(url);
                setFailed(false);
              }}
              onError={() => {
                loaded(null);
                setFailed(true);
              }}
            />
          )}
          <p
            role="status"
            className={`mt-3 text-sm leading-6 ${failed ? 'text-warning' : 'text-ink-muted'}`}
          >
            {failed
              ? copy.imageFailed
              : coverReady(value, loadedUrl)
                ? copy.imageReady
                : copy.imageRequired}
          </p>
        </>
      )}
    </fieldset>
  );
}
